// src/components/Header.js - Updated with account dropdown menu
import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import LoginModal from './LoginModal';
import RegisterModal from './RegisterModal';
import '../styles/Header.css';
import env from '../config/env'; // Import environment config

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [accountDropdownOpen, setAccountDropdownOpen] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const dropdownRef = useRef(null);
  const accountBtnRef = useRef(null);

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Handle route changes
  useEffect(() => {
    // Close mobile menu when route changes
    closeMobileMenu();
  }, [location.pathname]);

  // Handle menu body locking when menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      // Prevent body scrolling when menu is open
      document.body.classList.add('menu-open');
    } else {
      document.body.classList.remove('menu-open');
    }
    
    // Cleanup function
    return () => {
      document.body.classList.remove('menu-open');
    };
  }, [mobileMenuOpen]);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  // Handle escape key press to close menu and dropdown
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape') {
        if (accountDropdownOpen) {
          setAccountDropdownOpen(false);
        }
        if (mobileMenuOpen) {
          closeMobileMenu();
        }
      }
    };

    document.addEventListener('keydown', handleEscKey);
    return () => document.removeEventListener('keydown', handleEscKey);
  }, [mobileMenuOpen, accountDropdownOpen]);

  // Handle clicking outside of menu to close
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Close menu when clicking outside (but not on the toggle button)
      if (mobileMenuOpen &&
          !event.target.closest('.main-nav') &&
          !event.target.closest('.mobile-menu-toggle')) {
        closeMobileMenu();
      }
      // Close account dropdown when clicking outside
      if (accountDropdownOpen &&
          dropdownRef.current &&
          !dropdownRef.current.contains(event.target)) {
        setAccountDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [mobileMenuOpen, accountDropdownOpen]);

  const handleLogout = async () => {
    await logout();
    setAccountDropdownOpen(false);
    navigate('/');
  };

  const toggleAccountDropdown = () => {
    setAccountDropdownOpen(!accountDropdownOpen);
  };

  // Calculate dropdown position based on button position
  useEffect(() => {
    if (accountDropdownOpen && accountBtnRef.current && dropdownRef.current) {
      const dropdown = dropdownRef.current.querySelector('.account-dropdown');
      if (dropdown) {
        const btnRect = accountBtnRef.current.getBoundingClientRect();
        dropdown.style.top = `${btnRect.bottom + 10}px`;
        dropdown.style.right = `${window.innerWidth - btnRect.right}px`;
      }
    }
  }, [accountDropdownOpen]);

  return (
    <header className={`header ${isScrolled ? 'scrolled' : ''}`}>
      <div className="container header-container">
        <div className="logo-container">
          <Link to="/" onClick={closeMobileMenu}>
            {/* Use the regular logo URL from the env config */}
            <img 
              src={env.logoUrl} 
              alt="Legate" 
              className="logo"
            />
          </Link>
        </div>

        <nav id="mobile-nav" className={`main-nav ${mobileMenuOpen ? 'mobile-open' : ''}`}>
          <ul className="nav-list">
            <li className="nav-item">
              <Link 
                to="/" 
                className={location.pathname === '/' ? 'active' : ''} 
                onClick={closeMobileMenu}
              >
                Home
              </Link>
            </li>
            <li className="nav-item">
              <Link 
                to="/about" 
                className={location.pathname === '/about' ? 'active' : ''} 
                onClick={closeMobileMenu}
              >
                About
              </Link>
            </li>
            <li className="nav-item">
              <Link 
                to="/services" 
                className={location.pathname === '/services' ? 'active' : ''} 
                onClick={closeMobileMenu}
              >
                Services
              </Link>
            </li>
            <li className="nav-item">
              <Link 
                to="/blog" 
                className={location.pathname.includes('/blog') ? 'active' : ''} 
                onClick={closeMobileMenu}
              >
                Blog
              </Link>
            </li>
            <li className="nav-item">
              <Link 
                to="/downloads" 
                className={location.pathname === '/downloads' ? 'active' : ''} 
                onClick={closeMobileMenu}
              >
                Resources
              </Link>
            </li>
            <li className="nav-item">
              <Link 
                to="/collaboration" 
                className={location.pathname === '/collaboration' ? 'active' : ''} 
                onClick={closeMobileMenu}
              >
                Collaboration
              </Link>
            </li>
            <li className="nav-item">
              <Link
                to="/get-started"
                className={`${location.pathname === '/get-started' || location.pathname === '/service-form' ? 'active' : ''} btn-nav`}
                onClick={closeMobileMenu}
              >
                Get Started
              </Link>
            </li>
          </ul>
          <div className="account-menu" ref={dropdownRef}>
            {user ? (
              <>
                <button
                  ref={accountBtnRef}
                  className="account-icon-btn"
                  onClick={toggleAccountDropdown}
                  aria-label="Account menu"
                  aria-expanded={accountDropdownOpen}
                  aria-haspopup="true"
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                    <circle cx="12" cy="7" r="4"></circle>
                  </svg>
                </button>
                {accountDropdownOpen && (
                  <div className="account-dropdown" role="menu">
                    <div className="dropdown-header">
                      <span className="user-email">{user.email}</span>
                    </div>
                    <div className="dropdown-divider"></div>
                    <Link
                      to="/dashboard"
                      className="dropdown-item"
                      role="menuitem"
                      onClick={() => setAccountDropdownOpen(false)}
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                        <polyline points="9 22 9 12 15 12 15 22"></polyline>
                      </svg>
                      Dashboard
                    </Link>
                    <Link
                      to="/dashboard#settings"
                      className="dropdown-item"
                      role="menuitem"
                      onClick={() => setAccountDropdownOpen(false)}
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <circle cx="12" cy="12" r="3"></circle>
                        <path d="M12 1v6m0 6v6m4.22-13.22l4.24 4.24M1.54 12h6m6 0h6"></path>
                      </svg>
                      Settings
                    </Link>
                    <div className="dropdown-divider"></div>
                    <button
                      className="dropdown-item logout-item"
                      role="menuitem"
                      onClick={handleLogout}
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                        <polyline points="16 17 21 12 16 7"></polyline>
                        <line x1="21" y1="12" x2="9" y2="12"></line>
                      </svg>
                      Sign Out
                    </button>
                  </div>
                )}
              </>
            ) : (
              <button
                className="account-icon-btn login-btn"
                onClick={() => setShowLoginModal(true)}
                aria-label="Sign in"
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                  <circle cx="12" cy="7" r="4"></circle>
                </svg>
              </button>
            )}
          </div>
        </nav>

        <button
          className="mobile-menu-toggle"
          onClick={toggleMobileMenu}
          aria-label={mobileMenuOpen ? "Close navigation menu" : "Open navigation menu"}
          aria-expanded={mobileMenuOpen}
          aria-controls="mobile-nav"
        >
          <span></span>
          <span></span>
          <span></span>
        </button>
      </div>

      {/* Login/Register Modals */}
      <LoginModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onSwitchToRegister={() => {
          setShowLoginModal(false);
          setShowRegisterModal(true);
        }}
      />
      <RegisterModal
        isOpen={showRegisterModal}
        onClose={() => setShowRegisterModal(false)}
        onSwitchToLogin={() => {
          setShowRegisterModal(false);
          setShowLoginModal(true);
        }}
      />
    </header>
  );
};

export default Header;
